/**
 * The read paths of the resolved surface set: `useSurface(key)` and
 * `useSurfaceSet()` (ARCH-005-MOD-016: with `<SurfaceGate>`, the ONLY read
 * paths for a surface state in the tenant SPA).
 *
 * THE FRONTEND DERIVES NOTHING (SPEC-TENANT-SURFACES RULE-003). A key is usable
 * exactly when the backend resolver's answer lists it with a recognised state;
 * a key absent from the answer is unusable. `state` is handed through as the
 * server sent it so the advanced category (UI-003, `AdvancedGroup`) can PLACE a
 * block - it is never computed here from `advanced_enabled`, a permission or
 * the user's presentation preference (RULE-011).
 *
 * FAIL CLOSED (RULE-014, RULE-001). While the answer is loading, when the read
 * has failed (including a failed background refetch that still holds an older
 * answer - holding it would outlive the RULE-015 staleness bound), and for a
 * row whose state is not one API-001 defines, the key is NOT usable. There is
 * no default-visible path anywhere in this module.
 *
 * THE `configurable: false` LEG (RULE-014, SCEN-010; D-TSG-09 option A). A
 * pinned surface (the agent list, the agent landing page) MUST stay usable
 * when the read fails, so the user is never locked out of the shell. The ONLY
 * source of which keys those are is the SERVER: a 503 of API-001 carries
 * `details.always_usable`, the registry's `configurable: false` keys after the
 * release omission filter. It is read in the ERROR state only, and only from a
 * 503 body - never while loading, never from a 401 / 403, and a failure with
 * no response at all (network) keeps every key unusable. There is no client
 * copy of the registry (RULE-016); {@link usableWithoutAnswer} is the one
 * place this leg lives.
 *
 * A RETRY KEEPS THE ERROR STATE (RULE-014, SCEN-010). TanStack v5 puts a query
 * that failed with no data back into `pending` the moment it refetches (a
 * Retry, a reader mounting, a focus refetch) and clears its error - which
 * would read as `loading` here, make the pinned keys vanish from the shell
 * for the length of the retry, and drop the typed notice for a bare loader.
 * So the last failure of the surfaces query is remembered per QueryClient
 * ({@link lastFailure}) and, while a fetch is in flight on a query that has
 * failed and holds no answer, the set stays `error` with THAT failure: the
 * pinned keys stay usable, every other key stays unusable, and `isFetching`
 * says a retry is running. A success forgets it.
 *
 * EXTENSIBLE ON PURPOSE. Both hooks return objects so later waves add fields
 * without changing any call site.
 *
 * THE STAFF LEG (RULE-012, TASK-TSURF-W7-STAFF-BADGES). `useSurfaceSet()` also
 * hands through, from the SAME query, the answer's `unrestricted` flag and -
 * per key, through `tenantState(key)` - the TENANT's own tri-state, which is
 * the only input of the staff badges. Both are the server's words: nothing
 * here computes either one. `tenantState` answers ONLY inside a successful
 * answer whose `unrestricted` is true; a `tenant_state` that arrives in a
 * restricted answer is a server disclosure defect (SCEN-012), and it is
 * STRIPPED from `surfaces` and never returned, so no consumer can render a
 * badge from it. (`useSurface(key)` keeps its four fields: the staff leg is
 * read through the set.)
 *
 * @module surfaces/useSurface
 * @see SPEC-TENANT-SURFACES-RULE-003 / RULE-012 / RULE-014 / RULE-015
 * @see TASK-TSURF-W3-SURFACES-STORE-FE-CHG-002
 * @see TASK-TSURF-W7-STAFF-BADGES-CHG-001
 * @see DECISIONS.md D-TSG-09 (TASK-TSURF-W3-NAV-ROUTE-FILTER, orchestrator fold-in)
 */
import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import type { SurfaceKey } from "./keys";
import {
  classifySurfacesError,
  surfacesQueryOptions,
  type MeSurfacesResponse,
  type ResolvedSurfaceRow,
  type SurfaceState,
  type SurfaceTenantState,
  type SurfacesError,
} from "./surfacesQuery";

/** Where the resolved set stands. `error` is the scoped fail-closed state. */
export type SurfaceSetStatus = "loading" | "success" | "error";

/** The two states API-001 lets a listed key carry; anything else fails closed (RULE-001). */
function isRecognisedState(state: unknown): state is SurfaceState {
  return state === "visible" || state === "advanced";
}

/** The three values API-001 lets `tenant_state` carry; anything else reads as "no tenant state". */
function isRecognisedTenantState(state: unknown): state is SurfaceTenantState {
  return state === "visible" || state === "advanced" || state === "hidden";
}

const NO_KEYS: ReadonlySet<SurfaceKey> = new Set();

/**
 * The pinned keys a failed read declared usable (D-TSG-09): the
 * `details.always_usable` array of an API-001 503 body. Anything else - no
 * response (network), another status, a body without the array, a non-string
 * entry - declares NOTHING, so every key stays unusable (RULE-014).
 */
function alwaysUsableFrom(error: unknown): ReadonlySet<SurfaceKey> {
  if (!isAxiosError(error) || error.response?.status !== 503) return NO_KEYS;
  const body = error.response.data as { details?: { always_usable?: unknown } } | undefined;
  const list = body?.details?.always_usable;
  if (!Array.isArray(list)) return NO_KEYS;
  return new Set(list.filter((k): k is SurfaceKey => typeof k === "string"));
}

/**
 * Whether a key is usable when there is no current answer. While LOADING: no
 * key. On ERROR: exactly the keys the server's 503 declared always usable
 * (D-TSG-09); every other key, configurable or not, is unusable.
 */
function usableWithoutAnswer(key: SurfaceKey, pinned: ReadonlySet<SurfaceKey>): boolean {
  return pinned.has(key);
}

/**
 * The agent start page as the server answered it (D-TSG-32). Structurally the
 * `AgentStartConfig` of apps/tenant/landing.ts, which stays free of any import
 * from this module.
 */
export interface SurfaceAgentStart {
  tenantDefault: SurfaceKey | null;
  perAgent: Readonly<Record<string, SurfaceKey>>;
}

export interface SurfaceSet {
  status: SurfaceSetStatus;
  /**
   * The rows usable by this caller, keyed by surface key. EMPTY unless
   * `status` is `success` - never the last answer on error (RULE-014).
   */
  surfaces: Readonly<Record<SurfaceKey, ResolvedSurfaceRow>>;
  /** The tenant switch of the advanced tier as the server resolved it; false unless `success`. */
  advancedEnabled: boolean;
  /**
   * RULE-012(a): the caller resolves UNRESTRICTED (holds `ui.view_all`), exactly
   * as the server reported it; false unless `success`. Never derived here.
   */
  unrestricted: boolean;
  /**
   * The agent start page (D-TSG-32), handed through as the server sent it;
   * null unless `success`, so a landing never reads a stale or guessed one and
   * falls back to the default order.
   */
  agentStart: SurfaceAgentStart | null;
  /**
   * The TENANT's own resolution of `key` (RULE-012 DISCLOSURE) - the only input
   * of the staff badges. Null unless the set is a success whose `unrestricted`
   * is true AND the key's row carries a recognised `tenant_state`.
   */
  tenantState: (key: SurfaceKey) => SurfaceTenantState | null;
  /** The typed failure while `status` is `error`, else null. */
  error: SurfacesError | null;
  /** True while a fetch (first load, refetch or a Retry) is in flight. */
  isFetching: boolean;
  /** Is this key usable by the caller right now (same predicate as `useSurface`)? */
  isUsable: (key: SurfaceKey) => boolean;
  /** Re-read the set (the layouts' Retry). */
  refetch: () => void;
}

/**
 * The last failure of the surfaces query, per QueryClient: what the set keeps
 * showing while a refetch after an error is in flight (RULE-014, SCEN-010;
 * see the module comment). Written only from the query's own `error` state and
 * deleted on success, so it can never outlive an answer.
 */
const lastFailure = new WeakMap<QueryClient, unknown>();

const NO_SURFACES: Readonly<Record<SurfaceKey, ResolvedSurfaceRow>> = Object.freeze({});

/** The resolved map plus its status - the one shared read of the surfaces query. */
export function useSurfaceSet(): SurfaceSet {
  const queryClient = useQueryClient();
  const query = useQuery(surfacesQueryOptions());
  const { isFetching, refetch } = query;
  // TanStack v5 calls the no-answer-yet state "pending"; this API says "loading".
  let status: SurfaceSetStatus = query.status === "pending" ? "loading" : query.status;
  let error: unknown = query.error;
  if (query.status === "error") {
    lastFailure.set(queryClient, query.error);
  } else if (query.status === "success") {
    lastFailure.delete(queryClient);
  } else if (query.fetchStatus !== "idle" && query.errorUpdateCount > 0 && lastFailure.has(queryClient)) {
    // A refetch after a failure, with no answer yet: keep the error state and
    // its pinned keys rather than dropping to loading (RULE-014, SCEN-010).
    status = "error";
    error = lastFailure.get(queryClient);
  }
  const data: MeSurfacesResponse | undefined = query.data;

  const current = status === "success" ? data : undefined;
  // RULE-012: the server's flag, read as-is; only a literal `true` counts.
  const unrestricted = current ? current.unrestricted === true : false;

  const surfaces = useMemo<Readonly<Record<SurfaceKey, ResolvedSurfaceRow>>>(() => {
    if (!current) return NO_SURFACES;
    const out: Record<SurfaceKey, ResolvedSurfaceRow> = {};
    for (const [key, row] of Object.entries(current.surfaces)) {
      if (!row || !isRecognisedState(row.state)) continue;
      if (!unrestricted && row.tenant_state !== undefined) {
        // A restricted answer must not carry it (SCEN-012); never pass it on.
        const withheld: ResolvedSurfaceRow = { ...row };
        delete withheld.tenant_state;
        out[key] = withheld;
      } else {
        out[key] = row;
      }
    }
    return out;
  }, [current, unrestricted]);

  const agentStart = useMemo<SurfaceAgentStart | null>(() => {
    if (!current) return null;
    const start = current.agent_start_surface;
    const perAgent: Record<string, SurfaceKey> = {};
    const raw = current.agent_start_overrides;
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
      for (const [agentId, key] of Object.entries(raw)) {
        if (typeof key === "string" && key !== "") perAgent[agentId] = key;
      }
    }
    return { tenantDefault: typeof start === "string" && start !== "" ? start : null, perAgent };
  }, [current]);

  const tenantState = useCallback(
    (key: SurfaceKey): SurfaceTenantState | null => {
      if (!unrestricted || !Object.prototype.hasOwnProperty.call(surfaces, key)) return null;
      const state = surfaces[key].tenant_state;
      return isRecognisedTenantState(state) ? state : null;
    },
    [unrestricted, surfaces],
  );

  // Only in the error state; while loading nothing is usable (RULE-014).
  const pinned = useMemo(() => (status === "error" ? alwaysUsableFrom(error) : NO_KEYS), [status, error]);

  const isUsable = useCallback(
    (key: SurfaceKey): boolean =>
      current ? Object.prototype.hasOwnProperty.call(surfaces, key) : usableWithoutAnswer(key, pinned),
    [current, surfaces, pinned],
  );

  const doRefetch = useCallback(() => {
    void refetch();
  }, [refetch]);

  return {
    status,
    surfaces,
    advancedEnabled: current ? current.advanced_enabled === true : false,
    unrestricted,
    agentStart,
    tenantState,
    error: status === "error" ? classifySurfacesError(error) : null,
    isFetching,
    isUsable,
    refetch: doRefetch,
  };
}

export interface SurfaceResult {
  /** Render the surface only when true. */
  usable: boolean;
  /** The server's state for a usable key (`advanced` places it in the Advanced category); null otherwise. */
  state: SurfaceState | null;
  /** True until the first answer (or failure) arrives. */
  loading: boolean;
  /** The typed failure of the set, when the read failed. */
  error: SurfacesError | null;
}

/** The resolved answer for ONE surface key. */
export function useSurface(key: SurfaceKey): SurfaceResult {
  const set = useSurfaceSet();
  const row = set.status === "success" ? set.surfaces[key] : undefined;
  return {
    usable: set.isUsable(key),
    state: row ? row.state : null,
    loading: set.status === "loading",
    error: set.error,
  };
}
